/**
 * Instagram Ads Adapter (Story 9.5)
 *
 * Instagram Ads uses the SAME Meta Marketing API as Facebook Ads, with one
 * critical difference: a publisher_platform filter restricts results to
 * Instagram-placed ads only.
 *
 * Uses the user's long-lived access token (NOT Page Access Tokens).
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { discoverMetaAccounts, parseAppUsage } from "./discovery";
import { parseMetaGraphError, META_ATTRIBUTION_SETTING_MISMATCH_SUBCODE } from "./errors";
import { META_INTEGRATION_SCOPES } from "./scopes";
import {
  META_GRAPH_BASE,
  RATE_LIMIT_THRESHOLD,
  MAX_PAGES,
  FB_ADS_METRICS,
  FB_ADS_DIMENSIONS,
  handleMetaFetchError,
  mapDiscoverySectionFailure,
  parseMetricValue,
  parseActions,
  parseActionValues,
  parseCostPerAction,
  parsePurchaseRoas,
  parseVideoThruplay,
  parseActionArrayTotal,
  parseActionArrayFirst,
  parseActionsByType,
  parseCostByActionType,
  parseActionsByTypeWithWindows,
  parseCostByActionTypeWithWindows,
  emitPerWindowRecordFields,
  ensureAttributionSetting,
  writeAttributionSetting,
  appendAttributionParams,
  coerceAttributionSetting,
  isAdvantagePlusShoppingCampaign,
  ENABLE_PER_WINDOW_CAMPAIGN_METRICS,
  buildTimeRange,
  type MarketingInsightsResponse,
  type AttributionSetting,
} from "./facebook-ads";

// ============================================================================
// Constants
// ============================================================================

/**
 * Publisher platform filter — appended to ALL Marketing API insight queries.
 * This is THE key difference from Facebook Ads.
 */
const PUBLISHER_PLATFORM_FILTER = JSON.stringify([
  { field: "publisher_platform", operator: "IN", value: ["instagram"] },
]);

// ============================================================================
// Insights Fields (for the API call)
// ============================================================================

const ACCOUNT_INSIGHTS_FIELDS = [
  // Delivery
  "impressions", "reach", "frequency", "spend", "cpm", "cpp",
  "full_view_impressions", "social_spend",
  // Clicks
  "clicks", "ctr", "cpc", "inline_link_clicks", "inline_link_click_ctr",
  "outbound_clicks", "outbound_clicks_ctr", "cost_per_outbound_click",
  "unique_clicks", "unique_ctr", "cost_per_unique_click",
  // Conversions & Actions
  "actions", "action_values", "cost_per_action_type",
  "purchase_roas", "website_purchase_roas",
  // Video
  "video_thru_play_actions", "cost_per_thruplay",
  "cost_per_15_sec_video_view", "cost_per_2_sec_continuous_video_view",
  "video_p25_watched_actions", "video_p50_watched_actions",
  "video_p75_watched_actions", "video_p100_watched_actions",
  "video_continuous_2_sec_watched_actions",
  "video_15_sec_watched_actions", "video_30_sec_watched_actions",
  "video_avg_time_watched_actions", "video_play_actions",
  // Quality
  "quality_ranking", "engagement_rate_ranking", "conversion_rate_ranking",
  // Engagement
  "inline_post_engagement", "cost_per_inline_post_engagement",
].join(",");

const CAMPAIGN_INSIGHTS_FIELDS = [
  "campaign_name", "campaign_id",
  "impressions", "reach", "clicks", "spend",
  "actions", "action_values", "cost_per_action_type",
  "quality_ranking", "engagement_rate_ranking", "conversion_rate_ranking",
  // Story 35.5 — ASC discriminator inputs (mirrors FB Ads).
  "optimization_goal", "objective",
].join(",");

// ============================================================================
// Metric & Dimension Definitions (identical to Facebook Ads)
// ============================================================================

/** Instagram Ads uses the same metrics as Facebook Ads (same Marketing API) */
const IG_ADS_METRICS: MetricDefinition[] = FB_ADS_METRICS;

/**
 * Instagram Ads dimensions — same as Facebook Ads but WITHOUT publisher_platform
 * (always "instagram", used as filter not breakdown) and with "explore" added
 * to placement exampleValues.
 */
const IG_ADS_DIMENSIONS: DimensionDefinition[] = FB_ADS_DIMENSIONS
  .filter((d) => d.key !== "publisher_platform")
  .map((d) =>
    d.key === "placement"
      ? { ...d, exampleValues: ["feed", "stories", "reels", "explore"] }
      : d
  );

// ============================================================================
// Instagram Ads Adapter
// ============================================================================

export const instagramAdsAdapter: IntegrationAdapter = {
  platform: "instagram-ads",

  rateLimits: {
    requestsPerMinute: 60,
    requestsPerDay: 86400,
  },

  // --------------------------------------------------------------------------
  // fetchMetrics
  // --------------------------------------------------------------------------
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const adAccountId = ctx.accountId;
    if (!adAccountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No Ad Account selected. Please select an Ad Account first.",
        retryable: false,
      });
    }

    const token = credentials.accessToken;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    // Build time range (ISO dates, clamped to 37 months)
    const { timeRange, clamped } = buildTimeRange(dateRange.start, dateRange.end);

    if (clamped) {
      console.log(JSON.stringify({
        event: "ig_ads_date_clamped",
        adAccountId,
        tenantId: ctx.tenantId,
        originalStart: dateRange.start.toISOString(),
      }));
    }

    let rateLimitWarning = false;
    const encodedFilter = encodeURIComponent(PUBLISHER_PLATFORM_FILTER);

    // Story 35.5 — IG and FB Ads share the same ad account, so they reuse
    // the same attribution_setting KV key. Cache hit costs 0 API reads;
    // miss costs 1 probe. Branch shapes the insights URL identically to
    // the FB Ads adapter (action_attribution_windows vs.
    // use_account_attribution_setting).
    let attributionSetting = await ensureAttributionSetting(ctx, adAccountId, authHeaders);

    // --- Fetch account-level insights (with publisher_platform filter) ---
    const buildAccountUrl = (setting: AttributionSetting): string => {
      const base = `${META_GRAPH_BASE}/${adAccountId}/insights?fields=${ACCOUNT_INSIGHTS_FIELDS}&time_range=${encodeURIComponent(timeRange)}&time_increment=1&level=account&filtering=${encodedFilter}`;
      return appendAttributionParams(base, setting);
    };

    let accountUrl = buildAccountUrl(attributionSetting);
    let accountResult = await safeFetch(accountUrl, authHeaders, 25_000);

    // Story 35.5 — v26 subcode 2655 self-heal (one-shot retry). Mirrors
    // the FB Ads adapter logic — IG shares the ad account so the KV
    // update propagates to FB Ads on its next fetch too.
    if (
      !accountResult.success &&
      attributionSetting === "configured_at_call_time" &&
      accountResult.error.providerMessage
    ) {
      const parsedErr = parseMetaGraphError(accountResult.error.providerMessage);
      if (parsedErr?.errorSubcode === META_ATTRIBUTION_SETTING_MISMATCH_SUBCODE) {
        const originalSetting = attributionSetting;
        attributionSetting = "use_account_default";
        await writeAttributionSetting(ctx, attributionSetting);
        console.log(
          JSON.stringify({
            event: "meta_ads_attribution_setting_self_heal",
            adAccountId,
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            originalSetting,
            correctedSetting: attributionSetting,
            adapter: "instagram-ads",
          })
        );
        accountUrl = buildAccountUrl(attributionSetting);
        accountResult = await safeFetch(accountUrl, authHeaders, 25_000);
      }
    }

    if (!accountResult.success) {
      return handleMetaFetchError(accountResult);
    }

    const accountResponse = accountResult.data;
    const appUsage = parseAppUsage(accountResponse);
    if (appUsage > RATE_LIMIT_THRESHOLD) {
      rateLimitWarning = true;
      console.log(JSON.stringify({
        event: "meta_rate_limit_approaching",
        callCount: appUsage,
        adAccountId,
        tenantId: ctx.tenantId,
      }));
    }

    let accountData: MarketingInsightsResponse;
    try {
      accountData = (await accountResponse.json()) as MarketingInsightsResponse;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse Marketing API response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    // Check for API-level error in the JSON body
    if (accountData.error) {
      const metaError = parseMetaGraphError(JSON.stringify(accountData));
      if (metaError) {
        return adapterErr({
          code: metaError.code,
          message: sanitizeErrorMessage(metaError.message),
          retryable: metaError.retryable,
        });
      }
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(accountData.error.message),
        retryable: false,
      });
    }

    // Handle empty results gracefully (no Instagram-placed ads in account)
    if (!accountData.data || accountData.data.length === 0) {
      return adapterOk({
        platform: "instagram-ads",
        accountId: adAccountId,
        dateRange: { start: dateRange.start, end: dateRange.end },
        records: [],
        fetchedAt: new Date(),
        recordCount: 0,
      });
    }

    // Get currency from ad account (fetch account info)
    let currency = "USD";
    if (!rateLimitWarning) {
      const accountInfoUrl = `${META_GRAPH_BASE}/${adAccountId}?fields=currency`;
      const infoResult = await safeFetch(accountInfoUrl, authHeaders, 5_000);
      if (infoResult.success) {
        const infoAppUsage = parseAppUsage(infoResult.data);
        if (infoAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }
        try {
          const infoBody = (await infoResult.data.json()) as { currency?: string };
          if (infoBody.currency) {
            currency = infoBody.currency;
          }
        } catch {
          // Non-fatal — use default currency
        }
      }
    }

    // Transform account-level insights to daily summary records
    const records: Record<string, unknown>[] = [];

    // Handle pagination for account insights
    let accountPageData = accountData;
    let accountPageCount = 0;
    while (accountPageData && accountPageCount < MAX_PAGES) {
      for (const row of accountPageData.data ?? []) {
        // ── Delivery ──
        const impressions = parseMetricValue(row.impressions);
        const reach = parseMetricValue(row.reach);
        const frequency = parseMetricValue(row.frequency);
        const spend = parseMetricValue(row.spend);
        const cpm = parseMetricValue(row.cpm);
        const cpp = parseMetricValue(row.cpp);
        const fullViewImpressions = parseMetricValue(row.full_view_impressions);
        const socialSpend = parseMetricValue(row.social_spend);

        // ── Clicks ──
        const clicks = parseMetricValue(row.clicks);
        const linkClicks = parseMetricValue(row.inline_link_clicks);
        const ctr = parseMetricValue(row.ctr);
        const cpc = parseMetricValue(row.cpc);
        const inlineLinkClickCtr = parseMetricValue(row.inline_link_click_ctr);
        const outboundClicks = parseActionArrayTotal(row.outbound_clicks);
        const outboundClicksCtr = parseActionArrayFirst(row.outbound_clicks_ctr);
        const costPerOutboundClick = parseActionArrayFirst(row.cost_per_outbound_click);
        const uniqueClicks = parseMetricValue(row.unique_clicks);
        const uniqueCtr = parseMetricValue(row.unique_ctr);
        const costPerUniqueClick = parseMetricValue(row.cost_per_unique_click);

        // ── Conversions ──
        const { purchaseConversions } = parseActions(row.actions);
        const purchaseValue = parseActionValues(row.action_values);
        const costPerConversion = parseCostPerAction(row.cost_per_action_type);
        const purchaseRoas = parsePurchaseRoas(row.purchase_roas);
        const websitePurchaseRoas = parsePurchaseRoas(row.website_purchase_roas);

        // ── Action type breakdowns ──
        const actionsByType = parseActionsByType(row.actions);
        const costByType = parseCostByActionType(row.cost_per_action_type);

        // Story 35.5 — per-attribution-window breakdowns. IG mirrors FB's
        // per-window emission so the IG record shape matches the shared
        // FB_ADS_METRICS registry (IG_ADS_METRICS = FB_ADS_METRICS).
        const actionsByWindow = parseActionsByTypeWithWindows(row.actions);
        const costByWindow = parseCostByActionTypeWithWindows(row.cost_per_action_type);

        // ── Video ──
        const videoThruplay = parseVideoThruplay(row.video_thru_play_actions);
        const costPerThruplay = parseMetricValue(row.cost_per_thruplay);
        const costPer15sVideoView = parseActionArrayFirst(row.cost_per_15_sec_video_view);
        const costPer2sContinuousVideoView = parseActionArrayFirst(row.cost_per_2_sec_continuous_video_view);
        const videoP25 = parseActionArrayTotal(row.video_p25_watched_actions);
        const videoP50 = parseActionArrayTotal(row.video_p50_watched_actions);
        const videoP75 = parseActionArrayTotal(row.video_p75_watched_actions);
        const videoP100 = parseActionArrayTotal(row.video_p100_watched_actions);
        const video2sContinuous = parseActionArrayTotal(row.video_continuous_2_sec_watched_actions);
        const video15s = parseActionArrayTotal(row.video_15_sec_watched_actions);
        const video30s = parseActionArrayTotal(row.video_30_sec_watched_actions);
        const videoAvgTime = parseActionArrayTotal(row.video_avg_time_watched_actions);
        const videoPlays = parseActionArrayTotal(row.video_play_actions);

        // ── Engagement ──
        const inlinePostEngagement = parseMetricValue(row.inline_post_engagement);
        const costPerInlinePostEngagement = parseMetricValue(row.cost_per_inline_post_engagement);

        // ── Quality rankings ──
        const qualityRanking = row.quality_ranking ?? "";
        const engagementRateRanking = row.engagement_rate_ranking ?? "";
        const conversionRateRanking = row.conversion_rate_ranking ?? "";

        // ── Derived metrics — rounded for precision (Story 9.4 learning) ──
        // Use purchaseConversions (not totalConversions) — ads "conversions" should reflect purchases, not all actions
        const conversionRate = clicks > 0 ? Math.round((purchaseConversions / clicks) * 10000) / 10000 : 0;
        const videoThruplayRate = impressions > 0 ? Math.round((videoThruplay / impressions) * 10000) / 10000 : 0;
        const effectiveRoas = purchaseRoas > 0 ? purchaseRoas : (spend > 0 ? Math.round((purchaseValue / spend) * 100) / 100 : 0);
        const landingPageViews = actionsByType["landing_page_view"] ?? 0;
        const landingPageViewPerLinkClick = linkClicks > 0
          ? Math.round((landingPageViews / linkClicks) * 10000) / 100
          : 0;
        const resultRate = impressions > 0 ? (purchaseConversions / impressions) * 100 : 0;

        const dailyRecord: Record<string, unknown> = {
          _reportType: "dailySummary",
          date: row.date_start,
          // Delivery
          impressions, reach, frequency, spend, cpm, cpp,
          full_view_impressions: fullViewImpressions,
          social_spend: socialSpend,
          // Clicks
          clicks, link_clicks: linkClicks, ctr, cpc,
          inline_link_click_ctr: inlineLinkClickCtr,
          outbound_clicks: outboundClicks,
          outbound_clicks_ctr: outboundClicksCtr,
          cost_per_outbound_click: costPerOutboundClick,
          unique_clicks: uniqueClicks,
          unique_ctr: uniqueCtr,
          cost_per_unique_click: costPerUniqueClick,
          // Conversions
          conversions: purchaseConversions,
          conversion_rate: conversionRate,
          cost_per_conversion: costPerConversion > 0 ? costPerConversion : (purchaseConversions > 0 ? Math.round((spend / purchaseConversions) * 100) / 100 : 0),
          purchase_value: purchaseValue,
          purchase_roas: effectiveRoas,
          website_purchase_roas: websitePurchaseRoas,
          cost_per_result: costPerConversion > 0 ? costPerConversion : (purchaseConversions > 0 ? Math.round((spend / purchaseConversions) * 100) / 100 : 0),
          results: purchaseConversions,
          result_rate: resultRate,
          landing_page_views: landingPageViews,
          landing_page_view_per_link_click: landingPageViewPerLinkClick,
          // Action type breakdowns
          action_link_click: actionsByType["link_click"] ?? 0,
          action_post_engagement: actionsByType["post_engagement"] ?? 0,
          action_page_engagement: actionsByType["page_engagement"] ?? 0,
          action_video_view: actionsByType["video_view"] ?? 0,
          action_landing_page_view: actionsByType["landing_page_view"] ?? 0,
          action_purchase: actionsByType["offsite_conversion.fb_pixel_purchase"] ?? 0,
          action_lead: actionsByType["offsite_conversion.fb_pixel_lead"] ?? 0,
          action_add_to_cart: actionsByType["offsite_conversion.fb_pixel_add_to_cart"] ?? 0,
          action_initiate_checkout: actionsByType["offsite_conversion.fb_pixel_initiate_checkout"] ?? 0,
          action_complete_registration: actionsByType["offsite_conversion.fb_pixel_complete_registration"] ?? 0,
          action_view_content: actionsByType["offsite_conversion.fb_pixel_view_content"] ?? 0,
          action_omni_purchase: actionsByType["omni_purchase"] ?? 0,
          action_all_leads: actionsByType["lead"] ?? 0,
          action_app_install: actionsByType["app_install"] ?? 0,
          action_flow_complete: actionsByType["onsite_conversion.flow_complete"] ?? 0,
          cost_per_action_link_click: costByType["link_click"] ?? 0,
          cost_per_action_purchase: costByType["offsite_conversion.fb_pixel_purchase"] ?? 0,
          cost_per_action_lead: costByType["offsite_conversion.fb_pixel_lead"] ?? 0,
          // Video
          video_thruplay: videoThruplay,
          video_thruplay_rate: videoThruplayRate,
          cost_per_thruplay: costPerThruplay,
          cost_per_15_sec_video_view: costPer15sVideoView,
          cost_per_2_sec_continuous_video_view: costPer2sContinuousVideoView,
          video_p25: videoP25, video_p50: videoP50,
          video_p75: videoP75, video_p100: videoP100,
          video_2s_continuous: video2sContinuous,
          video_15s: video15s, video_30s: video30s,
          video_avg_time: videoAvgTime,
          video_plays: videoPlays,
          // Quality
          quality_ranking: qualityRanking,
          engagement_rate_ranking: engagementRateRanking,
          conversion_rate_ranking: conversionRateRanking,
          // Engagement
          inline_post_engagement: inlinePostEngagement,
          cost_per_inline_post_engagement: costPerInlinePostEngagement,
          currency,
        };

        // Story 35.5 — emit 56 per-window fields (24 action + 8 omni + 24 cost).
        emitPerWindowRecordFields(dailyRecord, actionsByWindow, costByWindow);

        records.push(dailyRecord);
      }

      // Follow pagination
      const nextUrl = accountPageData.paging?.next;
      if (!nextUrl || rateLimitWarning) break;

      accountPageCount++;
      const nextResult = await safeFetch(nextUrl, authHeaders, 25_000);
      if (!nextResult.success) break;

      const nextAppUsage = parseAppUsage(nextResult.data);
      if (nextAppUsage > RATE_LIMIT_THRESHOLD) {
        rateLimitWarning = true;
        break;
      }

      try {
        accountPageData = (await nextResult.data.json()) as MarketingInsightsResponse;
      } catch {
        break;
      }
    }

    // --- Fetch campaign-level insights (with publisher_platform filter) ---
    if (!rateLimitWarning) {
      const campaignUrl = `${META_GRAPH_BASE}/${adAccountId}/insights?fields=${CAMPAIGN_INSIGHTS_FIELDS}&time_range=${encodeURIComponent(timeRange)}&time_increment=1&level=campaign&filtering=${encodedFilter}`;

      let url: string | null = campaignUrl;
      let pageCount = 0;

      while (url && pageCount < MAX_PAGES) {
        const result = await safeFetch(url, authHeaders, 25_000);
        if (!result.success) break;

        const response = result.data;
        const campaignAppUsage = parseAppUsage(response);
        if (campaignAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
          console.log(JSON.stringify({
            event: "meta_rate_limit_approaching",
            callCount: campaignAppUsage,
            adAccountId,
            tenantId: ctx.tenantId,
            context: "campaign_insights",
          }));
          break;
        }

        let body: MarketingInsightsResponse;
        try {
          body = (await response.json()) as MarketingInsightsResponse;
        } catch {
          break;
        }

        if (body.error) break;

        for (const row of body.data ?? []) {
          const campaignPurchaseValue = parseActionValues(row.action_values);
          const campaignSpend = parseMetricValue(row.spend);
          const campaignConversions = parseActions(row.actions).purchaseConversions;
          const campaignImpressions = parseMetricValue(row.impressions);
          const campaignClicks = parseMetricValue(row.clicks);

          const campaignRecord: Record<string, unknown> = {
            _reportType: "campaigns",
            date: row.date_start,
            campaign_name: row.campaign_name ?? "",
            campaign_id: row.campaign_id ?? "",
            impressions: campaignImpressions,
            reach: parseMetricValue(row.reach),
            clicks: campaignClicks,
            spend: campaignSpend,
            conversions: campaignConversions,
            purchase_value: campaignPurchaseValue,
            purchase_roas: campaignSpend > 0 ? Math.round((campaignPurchaseValue / campaignSpend) * 100) / 100 : 0,
            ctr: campaignImpressions > 0 ? Math.round((campaignClicks / campaignImpressions) * 10000) / 10000 : 0,
            cpc: campaignClicks > 0 ? Math.round((campaignSpend / campaignClicks) * 100) / 100 : 0,
            quality_ranking: row.quality_ranking ?? "",
            engagement_rate_ranking: row.engagement_rate_ranking ?? "",
            conversion_rate_ranking: row.conversion_rate_ranking ?? "",
            currency,
          };

          // Story 35.5 — ASC dynamic_attribution discriminator (IG mirror).
          if (isAdvantagePlusShoppingCampaign(row)) {
            campaignRecord._dynamicAttribution = true;
          }

          // Story 35.5 — feature-flagged campaign-level per-window emission.
          if (ENABLE_PER_WINDOW_CAMPAIGN_METRICS && !campaignRecord._dynamicAttribution) {
            const campaignActionsByWindow = parseActionsByTypeWithWindows(row.actions);
            const campaignCostByWindow = parseCostByActionTypeWithWindows(row.cost_per_action_type);
            emitPerWindowRecordFields(campaignRecord, campaignActionsByWindow, campaignCostByWindow);
          }

          records.push(campaignRecord);
        }

        url = body.paging?.next ?? null;
        pageCount++;
      }
    }

    // Sort records by date
    records.sort((a, b) => String(a.date).localeCompare(String(b.date)));

    return adapterOk({
      platform: "instagram-ads",
      accountId: adAccountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  // --------------------------------------------------------------------------
  // validateConnection
  // --------------------------------------------------------------------------
  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const adAccountId = ctx.accountId;
    if (!adAccountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No Ad Account ID provided for connection validation.",
        retryable: false,
      });
    }

    // Story 35.5 — also probe v26 attribution_setting and persist to KV
    // so subsequent fetchMetrics() calls skip the re-probe. IG and FB Ads
    // share the same ad account → same KV key → one probe serves both.
    const url = `${META_GRAPH_BASE}/${adAccountId}?fields=id,name,account_status,currency,attribution_setting`;
    const result = await safeFetch(
      url,
      { headers: { Authorization: `Bearer ${credentials.accessToken}` } },
      5_000
    );

    if (!result.success) {
      return handleMetaFetchError(result);
    }

    try {
      const body = (await result.data.json()) as {
        account_status?: number;
        attribution_setting?: string;
      };
      if (body.account_status !== 1) {
        return adapterErr({
          code: "account_not_found",
          message: "Ad Account is not active",
          retryable: false,
        });
      }
      const setting = coerceAttributionSetting(body.attribution_setting);
      await writeAttributionSetting(ctx, setting);
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "Failed to parse Ad Account validation response",
        retryable: false,
      });
    }

    return adapterOk(true);
  },

  // --------------------------------------------------------------------------
  // discoverAccounts
  // --------------------------------------------------------------------------
  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Story 31.20: same ad-accounts slice as facebook-ads under the ONE Meta
    // grant, emitted with platform "instagram-ads" — the catalog UNIQUE index
    // includes `platform`, so the cross-platform duplication is the designed
    // shape (AC #5). Fetch ONLY the ad-accounts section (AC #1); require it
    // to be complete (AC #3).
    const discoveryResult = await discoverMetaAccounts({
      accessToken: credentials.accessToken,
      grantedScopes: credentials.scopes,
      sections: { pages: false, adAccounts: true, instagram: false },
    });

    const adAccountsStatus = discoveryResult.sectionStatus.adAccounts;
    if (adAccountsStatus !== "complete") {
      return mapDiscoverySectionFailure(adAccountsStatus, discoveryResult.firstError);
    }

    // Defensive dedupe by id, first occurrence wins (ADR-0004 §Anti-Patterns 4).
    // id is the FROZEN act_-prefixed form; metadata.accountId carries the
    // bare numeric account_id (AC #7).
    const accountsById = new Map<string, AccountInfo>();
    for (const account of discoveryResult.adAccounts) {
      if (accountsById.has(account.id)) continue;
      accountsById.set(account.id, {
        id: account.id,
        name: account.name,
        platform: "instagram-ads" as const,
        metadata: { currency: account.currency, accountId: account.accountId },
        // Fresh spread per row — display/consent metadata only (Story 31-4)
        scopes: [...META_INTEGRATION_SCOPES["instagram-ads"].scopes],
      });
    }

    return adapterOk([...accountsById.values()]);
  },

  // --------------------------------------------------------------------------
  // getAvailableMetrics
  // --------------------------------------------------------------------------
  getAvailableMetrics(): MetricDefinition[] {
    return IG_ADS_METRICS;
  },

  // --------------------------------------------------------------------------
  // getAvailableDimensions
  // --------------------------------------------------------------------------
  getAvailableDimensions(): DimensionDefinition[] {
    return IG_ADS_DIMENSIONS;
  },
};
